import { useDispatch, useSelector } from "react-redux";
import { RootState, AppDispatch } from "../redux/store";
import {
  addToWishlist,
  removeFromWishlist,
} from "../redux/slices/wishlistSlice";

/**
 * Custom React hook for managing the user's wishlist state and actions.
 *
 * This hook provides access to the wishlist's ID, items, loading and error states,
 * as well as functions to add, remove, toggle, and check products in the wishlist.
 *
 * @returns An object containing:
 * - `wishlistId`: The current wishlist's unique identifier.
 * - `items`: An array of product IDs currently in the wishlist.
 * - `loading`: Boolean indicating if wishlist operations are in progress.
 * - `error`: Any error encountered during wishlist operations.
 * - `add(productId: string)`: Adds a product to the wishlist.
 * - `remove(productId: string)`: Removes a product from the wishlist.
 * - `toggle(productId: string)`: Toggles a product's presence in the wishlist.
 * - `isInWishlist(productId: string): boolean`: Checks if a product is in the wishlist.
 */
export const useWishlist = () => {
  const dispatch = useDispatch<AppDispatch>();
  const { wishlistId, items, loading, error } = useSelector((state: RootState) => state.wishlist);

  const add = (productId: string) => {
    if (!wishlistId) return;
    dispatch(addToWishlist({ wishlistId, productId }));
  };

  const remove = (productId: string) => {
    if (!wishlistId) return;
    dispatch(removeFromWishlist({ wishlistId, productId }));
  };

  const toggle = (productId: string) => {
    if (!wishlistId) return;
    const exists = items.some(item => item.productId === productId);
    if (exists) remove(productId);
    else add(productId);
  };

  const isInWishlist = (productId: string): boolean => {
    return items.some(item => item.productId === productId);
  };

  return {
    wishlistId,
    items,
    loading,
    error,
    add,
    remove,
    toggle,
    isInWishlist,
  };
};
